import React from "react";
import { motion } from "framer-motion";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import emailjs from '@emailjs/browser';
import { FaMapMarkerAlt, FaEnvelope, FaPhone } from "react-icons/fa";

// Initialize EmailJS with your public key
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

const ContactCard = ({ title, content, link, external = false }) => (
  <motion.a
    href={link}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    className="bg-purple900/30 backdrop-blur-sm rounded-xl p-6 border border-gold/10
      hover:border-gold/30 transition-all duration-300 group cursor-pointer"
  >
    <h4 className="text-xl font-radon text-gold mb-2">{title}</h4>
    <p className="text-purple100/80 font-konnect group-hover:text-gold/90 transition-colors duration-300">
      {content}
    </p>
  </motion.a>
);

// Update Validation Schema
const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name is too short')
    .max(50, 'Name is too long')
    .required('Name is required'),
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  phone: Yup.string(),
  message: Yup.string()
    .required('Message is required')
});

const FormField = ({ name, label, type = "text", as, error, touched }) => (
  <div>
    <Field
      as={as}
      type={type}
      id={name}
      name={name}
      className={`w-full px-6 py-3 rounded-xl bg-purple900/50 border 
        ${error && touched ? 'border-red-500' : 'border-gold/20'}
        text-purple100/70 placeholder:text-purple100/50 focus:outline-none focus:border-gold/50
        font-konnect transition-all duration-300`}
      placeholder={label}
    />
    {error && touched && (
      <p className="mt-1 text-red-500 text-sm font-konnect">{error}</p>
    )}
  </div>
);

const ContactForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    try {
      const token = await executeRecaptcha('contact_form');
      
      if (!token) {
        throw new Error('Failed to verify reCAPTCHA');
      }

      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_email: 'support@kesariglow.in',
          from_name: values.name,
          from_email: values.email,
          phone: values.phone || 'Not provided',
          message: values.message,
          submit_date: new Date().toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            dateStyle: 'full',
            timeStyle: 'long'
          }),
          'g-recaptcha-response': token
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (result.text === 'OK') {
        setStatus({ success: true });
        resetForm();
      }
    } catch (error) {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={{
        name: '',
        email: '',
        phone: '',
        message: ''
      }}
      validationSchema={ContactSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting, status }) => (
        <Form className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <FormField
              name="name"
              label="Your Name"
              error={errors.name}
              touched={touched.name}
            />
            <FormField
              name="email"
              label="Your Email"
              type="email"
              error={errors.email}
              touched={touched.email}
            />
          </div>

          <FormField
            name="phone"
            label="Your Phone Number"
            type="tel"
            error={errors.phone}
            touched={touched.phone}
          />

          <FormField
            name="message"
            label="Your Message"
            as="textarea"
            error={errors.message}
            touched={touched.message}
          />

          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 rounded-full bg-gradient-to-r from-gold to-secondary
                text-purple900 font-radon text-lg font-medium tracking-wider
                transform hover:scale-105 transition-all duration-300
                hover:from-secondary hover:to-gold min-w-[200px]
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {/* Status Messages */}
            {status?.success && (
              <p className="mt-4 text-green-400 font-konnect">
                Message sent successfully!
              </p>
            )}
            {status?.success === false && (
              <p className="mt-4 text-red-400 font-konnect">
                Failed to send message. Please try again.
              </p>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default function Inquiry() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'body'
      }}
    >
      <section id="inquiry" className="relative py-20 bg-gradient-to-b from-purple900 to-purple800 overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="relative container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-radon text-gold mb-6 font-medium">
              Get in Touch
            </h2>
            <p className="text-lg md:text-xl text-secondary/80 font-konnect max-w-2xl mx-auto">
              Have questions about our products? We'd love to hear from you
            </p>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-16 grid md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center"
          >
            {/* Email */}
            <ContactCard
              title="Email"
              content="support@kesariglow.in"
              link="mailto:support@kesariglow.in"
            />

            {/* Phone */}
            <ContactCard
              title="Phone"
              content="+91 98988 46514"
              link="tel:+919898846514"
            />

            {/* Location */}
            <ContactCard
              title="Location"
              content="Surat, Gujarat, India"
              link="https://maps.app.goo.gl/pzF46qdeWjsGkgpA8"
              external={true}
            />
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto mt-16"
          >
            <ContactForm />
          </motion.div>
        </div>
      </section>
    </GoogleReCaptchaProvider>
  );
} 